/**
 * WABA Locked Banner
 *
 * Surfaces when the currently-selected WhatsApp Business Account has been
 * locked by Meta (most often: business not yet verified on a coexistence
 * account, or a policy / payment issue).
 *
 * The banner is informational — the lock can only be lifted on Meta's side
 * (Business Manager → Security Center → Business Verification, or appeal
 * via WhatsApp Manager → Account Quality).
 *
 * Modeled on TrialBanner. Dismissible per session — the underlying
 * `lock_status` re-flips on next inbound page load so a dismissal is just
 * "I've acknowledged this for now."
 */

import React, { useState } from 'react';
import { ShieldAlert, X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface WabaLockedBannerProps {
  accountName?: string | null;
  phoneNumber?: string | null;
  onDismiss?: () => void;
}

const META_SECURITY_CENTER_URL =
  'https://business.facebook.com/security_center';
const META_WA_ACCOUNT_QUALITY_URL =
  'https://business.facebook.com/wa/manage/insights';

export const WabaLockedBanner: React.FC<WabaLockedBannerProps> = ({
  accountName,
  phoneNumber,
  onDismiss,
}) => {
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  const label =
    accountName && phoneNumber
      ? `${accountName} (${phoneNumber})`
      : accountName || phoneNumber || 'Your WhatsApp account';

  return (
    <div
      className="w-full bg-red-600 text-white px-4 py-2.5 flex items-center justify-between gap-3 z-50"
      data-testid="waba-locked-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <ShieldAlert className="h-4 w-4 flex-shrink-0" />
        <span className="text-sm font-medium truncate">
          {label} is locked by Meta. Sending is paused until your business is
          verified.
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button
          size="sm"
          variant="secondary"
          className="bg-white text-red-600 hover:bg-red-50 text-xs font-semibold"
          asChild
        >
          <a
            href={META_SECURITY_CENTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="waba-locked-banner-verify-cta"
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1" />
            Verify business
          </a>
        </Button>
        <a
          href={META_WA_ACCOUNT_QUALITY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline text-xs underline hover:text-red-100"
          data-testid="waba-locked-banner-learn-more"
        >
          Account quality
        </a>
        <button
          onClick={() => {
            setDismissed(true);
            onDismiss?.();
          }}
          className="p-1 rounded-full hover:bg-red-500 transition-colors"
          aria-label="Dismiss banner"
          data-testid="waba-locked-banner-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
